/**
 * PhoneWeb — подтверждение номера телефона (шаг Flow step="phone").
 * Два способа: подтверждение номера аккаунта Telegram (requestContact → опрос бэка → модалка)
 * и звонок-сброс на российский номер (SMSC waitcall → экран WaitCall).
 * Пропсы: onBack(); onNext(req, phone) — звонковый путь; onVerified(phone) — Telegram-путь.
 */
import { useCallback, useMemo, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { Card, FallbackButton, Screen } from "@/components/Screen";
import { Button } from "@/components/Button";
import { BackLink } from "@/components/BackLink";
import { Hero } from "@/components/Hero";
import { IconTile } from "@/components/IconTile";
import { Note } from "@/components/Note";
import { Field } from "@/components/Field";
import { Modal } from "@/components/Modal";
import { digitsOnly, formatPhoneRu } from "@/lib/format";
import { authApi, friendlyError, type WaitCallRequest } from "@/lib/api";
import { canRequestContact, platform, tg } from "@/telegram/tg";

type Props = {
  onBack: () => void;
  onNext: (req: WaitCallRequest, phone: string) => void; // звонковый путь → экран WaitCall
  onVerified: (phone: string) => void; // Telegram-путь → номер уже подтверждён
};

export function PhoneWeb({ onBack, onNext, onVerified }: Props) {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Telegram-путь
  const [tgBusy, setTgBusy] = useState(false);
  const [sharedPhone, setSharedPhone] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const inTelegram = platform() === "telegram";
  const showTgButton = canRequestContact();

  const digits = useMemo(() => digitsOnly(phone), [phone]);
  const valid = digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"));
  const normalized = valid ? "+7" + digits.slice(-10) : "";

  const submitCall = useCallback(async () => {
    if (!valid || loading) return;
    setLoading(true);
    setErr(null);
    try {
      const req = await authApi.request(normalized);
      onNext(req, normalized);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [valid, loading, normalized, onNext]);

  // Telegram: запросить номер аккаунта → дождаться заверенного контакта на бэке → модалка.
  // Источник истины — Contact, полученный ботом (webhook); колбэк только триггерит опрос.
  const shareTelegram = useCallback(() => {
    const wa = tg();
    if (!wa?.requestContact || tgBusy) return;
    setErr(null);
    setTgBusy(true);
    wa.requestContact(async (result) => {
      // форма ответа зависит от версии Telegram — явную отмену трактуем как отказ
      const cancelled =
        result === false ||
        (!!result && typeof result === "object" && (result as { status?: string }).status === "cancelled");
      if (cancelled) {
        setTgBusy(false);
        return;
      }
      try {
        let got: string | null = null;
        for (let i = 0; i < 15 && !got; i++) {
          got = (await authApi.telegramShared()).phone;
          if (!got) await new Promise((r) => setTimeout(r, 800));
        }
        if (got) setSharedPhone(got);
        else setErr("Не удалось получить номер. Попробуйте ещё раз или подтвердите звонком.");
      } catch (e) {
        setErr(friendlyError(e));
      } finally {
        setTgBusy(false);
      }
    });
  }, [tgBusy]);

  const confirmShared = useCallback(async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      const r = await authApi.telegramConfirm();
      onVerified(r.phone);
    } catch (e) {
      setErr(friendlyError(e));
      setSharedPhone(null);
    } finally {
      setConfirming(false);
    }
  }, [confirming, onVerified]);

  useBackButton(onBack);
  useMainButton({
    text: loading ? "Запрашиваем..." : "Подтвердить звонком",
    onClick: submitCall,
    active: valid && !sharedPhone,
    loading,
  });

  return (
    <Screen>
      <Hero icon="📱" title="Подтвердите номер" subtitle="Выберите удобный способ" />

      {showTgButton && (
        <Card>
          <MethodHead icon="✈️" tint="from-sky-400/25 to-sky-500/10" title="Из Telegram" desc="Любой номер, включая иностранный — мгновенно" />
          <Button onClick={shareTelegram} disabled={tgBusy}>
            {tgBusy ? "Получаем номер…" : "Подтвердить номер из Telegram"}
          </Button>
        </Card>
      )}

      {showTgButton && (
        <div className="my-5 flex items-center gap-3 text-[13px] text-white/40">
          <div className="h-px flex-1 bg-white/10" />
          или
          <div className="h-px flex-1 bg-white/10" />
        </div>
      )}

      {inTelegram && !showTgButton && (
        <Note className="mb-5">
          Обновите Telegram, чтобы подтвердить номер вашего аккаунта в один тап — или подтвердите
          российский номер звонком ниже.
        </Note>
      )}

      {!inTelegram && (
        <Note className="mb-5">
          Нет российского номера? Откройте приложение в Telegram, чтобы подтвердить номер вашего
          аккаунта.
        </Note>
      )}

      <Card>
        <MethodHead icon="📞" tint="from-emerald-400/25 to-emerald-500/10" title="Звонком на номер +7" desc="Бесплатный звонок-сброс, без SMS" />
        <Field
          type="tel"
          inputMode="tel"
          placeholder="+7 (___) ___-__-__"
          value={formatPhoneRu(phone)}
          onChange={(e) => setPhone(e.target.value)}
          error={err ?? undefined}
        />
      </Card>
      <BackLink onClick={onBack} className="mt-3 w-full py-2 text-center">
        Назад
      </BackLink>
      <FallbackButton
        text={loading ? "Запрашиваем..." : "Подтвердить звонком"}
        onClick={submitCall}
        disabled={!valid}
        loading={loading}
      />

      <Modal
        open={!!sharedPhone}
        title="Подтвердите номер"
        confirmText="Подтвердить"
        onConfirm={confirmShared}
        onCancel={() => {
          if (!confirming) setSharedPhone(null);
        }}
        loading={confirming}
      >
        Ваш номер: <span className="font-semibold text-white">{sharedPhone}</span>. Он будет
        привязан к вашему аккаунту.
      </Modal>
    </Screen>
  );
}

function MethodHead({ icon, tint, title, desc }: { icon: string; tint: string; title: string; desc: string }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <IconTile icon={icon} tint={tint} className="h-10 w-10 text-[18px]" />
      <div className="min-w-0">
        <div className="text-[15px] font-medium">{title}</div>
        <div className="text-[12px] text-white/45">{desc}</div>
      </div>
    </div>
  );
}
